import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Sparkles, LayoutTemplate } from 'lucide-react';
import { Header } from '../components/Header';
import { API_URL, cn } from '../lib/utils';

function CreateQuiz() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const template = location.state?.template;
  
  const [formData, setFormData] = useState({
    name: '',
    niche: template?.inputs?.niche || '',
    targetAudience: template?.inputs?.targetAudience || '',
    goal: 'lead_capture',
    questionCount: 10,
    questionTypes: ['multiple_choice', 'yes_no', 'slider'],
    captureEmail: true
  });

  const goalOptions = [
    { id: 'lead_capture', label: 'Lead Capture', desc: 'Collect emails and qualify leads' },
    { id: 'engagement', label: 'Engagement', desc: 'Boost profile visibility and shares' },
    { id: 'education', label: 'Education', desc: 'Help audience learn something new' },
    { id: 'assessment', label: 'Assessment', desc: 'Help audience evaluate themselves' }
  ];

  const questionTypeOptions = [
    { id: 'multiple_choice', label: 'Multiple Choice' },
    { id: 'yes_no', label: 'Yes / No' },
    { id: 'slider', label: 'Slider (1-10)' },
    { id: 'dropdown', label: 'Dropdown' }
  ];

  const validateText = (value, fieldName) => {
    if (!value || !value.trim()) {
      return `${fieldName} is required`;
    }
    if (/^\d+$/.test(value.trim())) {
      return `${fieldName} cannot be only numbers`;
    }
    if (value.trim().length < 5) {
      return `${fieldName} should be at least 5 characters`;
    }
    return null;
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      const nameError = validateText(formData.name, 'Quiz Name');
      if (nameError) newErrors.name = nameError;
      
      const nicheError = validateText(formData.niche, 'Topic/Niche');
      if (nicheError) newErrors.niche = nicheError;
    }
    
    if (stepNum === 2) {
      const audienceError = validateText(formData.targetAudience, 'Target Audience');
      if (audienceError) newErrors.targetAudience = audienceError;
    }
    
    if (stepNum === 3) {
      if (formData.questionTypes.length === 0) {
        newErrors.questionTypes = 'Select at least one question type';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/quizzes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          niche: formData.niche,
          target_audience: formData.targetAudience,
          goal: formData.goal,
          question_count: formData.questionCount,
          question_types: formData.questionTypes,
          capture_email: formData.captureEmail
        })
      });
      
      if (response.ok) {
        const quiz = await response.json();
        navigate(`/quiz/${quiz.quiz_id}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create quiz');
      }
    } catch (error) {
      alert('Failed to create quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const isStepValid = () => {
    if (step === 1) return formData.name.trim() && formData.niche.trim();
    if (step === 2) return formData.targetAudience.trim() && formData.goal;
    return formData.questionTypes.length > 0;
  };

  const toggleQuestionType = (type) => {
    if (formData.questionTypes.includes(type)) {
      setFormData({ ...formData, questionTypes: formData.questionTypes.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, questionTypes: [...formData.questionTypes, type] });
    }
    if (errors.questionTypes) setErrors({ ...errors, questionTypes: null });
  };

  return (
    <div className="min-h-screen bg-main">
      <Header />
      
      {template && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <LayoutTemplate className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-emerald-500">Using {template.name} template</span>
          </div>
        </div>
      )}

      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", s <= step ? "bg-emerald-500 text-white" : "bg-card-hover text-muted")}>
                  {s}
                </div>
                <span className={cn("text-sm hidden sm:inline", s <= step ? "text-foreground" : "text-muted")}>
                  {s === 1 ? "Basics" : s === 2 ? "Audience" : "Questions"}
                </span>
                {s < 3 && <div className="flex-1 h-0.5 bg-border ml-2" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Create Your Quiz</h2>
                <p className="text-muted">Give your quiz a name and define the topic.</p>
              </div>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Quiz Name *</label>
                  <input 
                    type="text" 
                    value={formData.name} 
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) setErrors({ ...errors, name: null });
                    }}
                    placeholder="e.g., Are You Ready to Scale Your Business?" 
                    className={cn("input w-full", errors.name && "border-red-500")} 
                  />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Topic / Niche *</label>
                  <input 
                    type="text" 
                    value={formData.niche} 
                    onChange={(e) => {
                      setFormData({ ...formData, niche: e.target.value });
                      if (errors.niche) setErrors({ ...errors, niche: null });
                    }}
                    placeholder="e.g., B2B SaaS Growth" 
                    className={cn("input w-full", errors.niche && "border-red-500")} 
                  />
                  {errors.niche && <p className="text-xs text-red-500 mt-1">{errors.niche}</p>}
                </div>
              </div>
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Who is this quiz for?</h2>
                <p className="text-muted">Define your audience and the quiz goal.</p>
              </div>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Target Audience *</label>
                  <input 
                    type="text" 
                    value={formData.targetAudience} 
                    onChange={(e) => {
                      setFormData({ ...formData, targetAudience: e.target.value });
                      if (errors.targetAudience) setErrors({ ...errors, targetAudience: null });
                    }}
                    placeholder="e.g., Startup founders looking to scale from $1M to $10M ARR" 
                    className={cn("input w-full", errors.targetAudience && "border-red-500")} 
                  />
                  {errors.targetAudience && <p className="text-xs text-red-500 mt-1">{errors.targetAudience}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Quiz Goal *</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {goalOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setFormData({ ...formData, goal: option.id })}
                        className={cn(
                          "p-4 rounded-xl border text-left transition-all",
                          formData.goal === option.id
                            ? "border-emerald-500 bg-emerald-500/10"
                            : "border-border bg-card hover:border-emerald-500/50"
                        )}
                      >
                        <p className="font-medium text-foreground">{option.label}</p>
                        <p className="text-xs text-muted mt-1">{option.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Configure Questions</h2>
                <p className="text-muted">Choose question types and settings.</p>
              </div>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Number of Questions</label>
                  <div className="flex items-center gap-4">
                    <input 
                      type="range" 
                      min="5" 
                      max="20" 
                      value={formData.questionCount} 
                      onChange={(e) => setFormData({ ...formData, questionCount: parseInt(e.target.value) })} 
                      className="flex-1"
                    />
                    <span className="text-lg font-semibold text-foreground w-8">{formData.questionCount}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Question Types *</label>
                  <div className="flex flex-wrap gap-2">
                    {questionTypeOptions.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => toggleQuestionType(type.id)}
                        className={cn(
                          "px-4 py-2 rounded-lg font-medium text-sm transition-colors",
                          formData.questionTypes.includes(type.id)
                            ? "bg-emerald-500 text-white"
                            : "bg-card-hover text-muted hover:text-foreground"
                        )}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                  {errors.questionTypes && <p className="text-xs text-red-500 mt-1">{errors.questionTypes}</p>}
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.captureEmail}
                      onChange={(e) => setFormData({ ...formData, captureEmail: e.target.checked })}
                      className="w-5 h-5 rounded border-border"
                    />
                    <span className="text-foreground">Capture name and email before showing results</span>
                  </label>
                  <p className="text-xs text-muted mt-1 ml-8">Recommended for lead generation</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        <div className="flex justify-between mt-10 pt-6 border-t border-border">
          <button onClick={() => step > 1 ? setStep(step - 1) : navigate('/dashboard')} className="btn-secondary">
            {step > 1 ? 'Previous' : 'Cancel'}
          </button>
          {step < 3 ? (
            <button onClick={handleNext} disabled={!isStepValid()} className={cn("btn-primary bg-emerald-500 hover:bg-emerald-600", !isStepValid() && "opacity-50")}>
              Continue
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading || !isStepValid()} className="btn-primary bg-emerald-500 hover:bg-emerald-600 flex items-center gap-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate Quiz</>}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

export default CreateQuiz;
